import { useCallback, useEffect, useMemo, useState } from 'react'
import { Copy, Link2 } from 'lucide-react'
import { adminApi } from '../services/api'
import { useColors } from '../hooks/useColors'

const TAG_OPTIONS = ['vip', 'corporate', 'bulk', 'new', 'regular', 'at-risk']

const QUICK_TEMPLATES = [
  {
    label: 'Festival Offer',
    message: 'Diwali offer: 30% off on dry cleaning! Use code DIWALI30',
  },
  {
    label: 'New Service',
    message: 'New service alert: Express Steam Iron now offers approx. 40-minute delivery for select areas.',
  },
  {
    label: 'Feedback Request',
    message: 'Thanks for choosing Rapidry. Please reply with your feedback so we can keep improving our service.',
  },
]

type BroadcastRecipient = {
  customer_id?: string
  customer_name?: string
  phone?: string
  url?: string
}

type BroadcastResult = {
  recipients_count?: number
  message_preview?: string
  wa_links?: BroadcastRecipient[]
  recipient_details?: BroadcastRecipient[]
  phones?: string[]
  recipients?: BroadcastRecipient[]
}

const CARD_SHADOW = '0px 2px 8px rgba(0,0,0,0.04)'
const WHATSAPP_CHAR_LIMIT = 4096

const formatPhoneList = (phones: string[]) => phones.join(', ')

const normalizePhone = (value: string) => value.replace(/[^0-9+]/g, '')

export default function BroadcastPage() {
  const colors = useColors()
  const [message, setMessage] = useState('')
  const [selectedTags, setSelectedTags] = useState<string[]>([])
  const [minOrders, setMinOrders] = useState('')
  const [lastOrderWithinDays, setLastOrderWithinDays] = useState('')
  const [preview, setPreview] = useState<BroadcastResult | null>(null)
  const [loadingPreview, setLoadingPreview] = useState(false)
  const [sending, setSending] = useState(false)
  const [error, setError] = useState('')
  const [copied, setCopied] = useState(false)

  const filter = useMemo(
    () => ({
      tags: selectedTags,
      min_orders: minOrders ? Number(minOrders) : undefined,
      last_order_within_days: lastOrderWithinDays ? Number(lastOrderWithinDays) : undefined,
    }),
    [lastOrderWithinDays, minOrders, selectedTags],
  )

  const loadPreview = useCallback(async () => {
    const trimmedMessage = message.trim()

    if (!trimmedMessage) {
      setPreview(null)
      setError('')
      return
    }

    setLoadingPreview(true)
    setError('')

    try {
      const response = await adminApi.sendBroadcast({
        message: trimmedMessage,
        channel: 'whatsapp',
        filter,
      })

      setPreview(response?.data ?? response)
    } catch (requestError) {
      setError('Unable to load preview. Please try again.')
    } finally {
      setLoadingPreview(false)
    }
  }, [filter, message])

  useEffect(() => {
    const timeout = window.setTimeout(() => {
      void loadPreview()
    }, 350)

    return () => window.clearTimeout(timeout)
  }, [loadPreview])

  const toggleTag = (tag: string) => {
    setSelectedTags((current) =>
      current.includes(tag)
        ? current.filter((item) => item !== tag)
        : [...current, tag],
    )
  }

  const copyText = async (text: string) => {
    await navigator.clipboard.writeText(text)
    setCopied(true)
    window.setTimeout(() => setCopied(false), 1500)
  }

  const handleGenerate = async () => {
    setSending(true)
    try {
      await loadPreview()
    } finally {
      setSending(false)
    }
  }

  const recipientCount = preview?.recipients_count || 0
  const recipientDetails = preview?.recipient_details || []
  const phones = preview?.phones || []

  return (
    <div style={{ padding: '24px 32px' }}>
      <div style={{ marginBottom: '18px' }}>
        <h1 className="font-display" style={{ margin: 0, fontSize: '26px', color: colors.textPrimary }}>
          Broadcast
        </h1>
        <p className="font-body" style={{ margin: '6px 0 0', color: colors.textMuted }}>
          Build a targeted recipient list, then open each WhatsApp chat yourself. <strong>Nothing is sent
          automatically</strong>. This generates ready-to-send WhatsApp links.
        </p>
      </div>

      <div style={{ display: 'grid', gap: '20px', gridTemplateColumns: 'minmax(0, 1.1fr) minmax(0, 0.9fr)' }}>
        <section style={{ background: colors.bgCard, borderRadius: '18px', padding: '22px', boxShadow: CARD_SHADOW, border: `1px solid ${colors.border}` }}>
          <div style={{ display: 'grid', gap: '18px' }}>
            <div>
              <p className="font-body" style={{ margin: '0 0 10px', fontSize: '12px', fontWeight: 700, color: colors.textMuted, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                Recipient Filter
              </p>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginBottom: '12px' }}>
                <button
                  type="button"
                  className="font-body"
                  onClick={() => setSelectedTags([])}
                  style={{
                    borderRadius: '999px',
                    border: selectedTags.length === 0 ? `1px solid ${colors.textPrimary}` : `1px solid ${colors.border}`,
                    background: selectedTags.length === 0 ? colors.textPrimary : colors.bgCard,
                    color: selectedTags.length === 0 ? colors.surface : colors.textSecondary,
                    padding: '8px 12px',
                    cursor: 'pointer',
                    fontWeight: 600,
                  }}
                >
                  All Customers
                </button>
                {TAG_OPTIONS.map((tag) => {
                  const active = selectedTags.includes(tag)
                  return (
                    <button
                      key={tag}
                      type="button"
                      className="font-body"
                      onClick={() => toggleTag(tag)}
                      style={{
                        borderRadius: '999px',
                        border: active ? `1px solid ${colors.accent}` : `1px solid ${colors.border}`,
                        background: active ? colors.surface : colors.bgCard,
                        color: active ? colors.textPrimary : colors.textSecondary,
                        padding: '8px 12px',
                        cursor: 'pointer',
                        fontWeight: 600,
                        textTransform: 'capitalize',
                      }}
                    >
                      {tag.replace('-', ' ')}
                    </button>
                  )
                })}
              </div>

              <div style={{ display: 'grid', gap: '12px', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))' }}>
                <div>
                  <label className="font-body" style={{ display: 'block', marginBottom: '6px', fontSize: '12px', color: colors.textMuted }}>
                    Minimum Orders
                  </label>
                  <input
                    type="number"
                    min="0"
                    value={minOrders}
                    onChange={(event) => setMinOrders(event.target.value)}
                    className="font-body"
                    style={{ width: '100%', height: '42px', border: `1.5px solid ${colors.border}`, borderRadius: '10px', padding: '0 12px' }}
                    placeholder="e.g. 3"
                  />
                </div>
                <div>
                  <label className="font-body" style={{ display: 'block', marginBottom: '6px', fontSize: '12px', color: colors.textMuted }}>
                    Last Active Within Days
                  </label>
                  <input
                    type="number"
                    min="0"
                    value={lastOrderWithinDays}
                    onChange={(event) => setLastOrderWithinDays(event.target.value)}
                    className="font-body"
                    style={{ width: '100%', height: '42px', border: `1.5px solid ${colors.border}`, borderRadius: '10px', padding: '0 12px' }}
                    placeholder="e.g. 30"
                  />
                </div>
              </div>
            </div>

            <div>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '10px', gap: '12px' }}>
                <p className="font-body" style={{ margin: 0, fontSize: '12px', fontWeight: 700, color: colors.textMuted, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                  Message
                </p>
                <p className="font-body" style={{ margin: 0, fontSize: '12px', color: message.length > WHATSAPP_CHAR_LIMIT ? '#B91C1C' : colors.textMuted }}>
                  {message.length} / {WHATSAPP_CHAR_LIMIT} characters
                </p>
              </div>
              {message.length > WHATSAPP_CHAR_LIMIT ? (
                <p className="font-body" style={{ margin: '0 0 8px', fontSize: '12px', color: '#B91C1C' }}>
                  ⚠ WhatsApp messages are limited to {WHATSAPP_CHAR_LIMIT} characters. Shorten the message before generating links.
                </p>
              ) : null}

              <textarea
                value={message}
                onChange={(event) => setMessage(event.target.value)}
                placeholder="Diwali offer: 30% off on dry cleaning! Use code DIWALI30"
                className="font-body"
                style={{ width: '100%', minHeight: '150px', border: `1.5px solid ${colors.border}`, borderRadius: '14px', padding: '14px', fontSize: '14px', lineHeight: 1.6, resize: 'vertical' }}
              />
            </div>

            <div>
              <p className="font-body" style={{ margin: '0 0 10px', fontSize: '12px', fontWeight: 700, color: colors.textMuted, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                Quick Fill
              </p>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                {QUICK_TEMPLATES.map((template) => (
                  <button
                    key={template.label}
                    type="button"
                    className="font-body"
                    onClick={() => setMessage(template.message)}
                    style={{
                      border: `1px solid ${colors.border}`,
                      background: colors.bgCard,
                      borderRadius: '999px',
                      padding: '8px 12px',
                      cursor: 'pointer',
                      fontWeight: 600,
                    }}
                  >
                    {template.label}
                  </button>
                ))}
              </div>
            </div>

            <button
              type="button"
              className="font-body"
              onClick={() => void handleGenerate()}
              disabled={sending || loadingPreview || !message.trim() || message.length > WHATSAPP_CHAR_LIMIT}
              style={{
                height: '46px',
                borderRadius: '999px',
                border: 'none',
                background: colors.textPrimary,
                color: colors.surface,
                fontWeight: 700,
                cursor: sending || loadingPreview || !message.trim() || message.length > WHATSAPP_CHAR_LIMIT ? 'not-allowed' : 'pointer',
                opacity: sending || loadingPreview || !message.trim() || message.length > WHATSAPP_CHAR_LIMIT ? 0.6 : 1,
              }}
            >
              {sending || loadingPreview ? 'Finding recipients…' : 'Preview recipients'}
            </button>

            {error ? (
              <div className="font-body" style={{ background: '#FEF2F2', color: '#991B1B', border: '1px solid #FCA5A5', borderRadius: '12px', padding: '12px 14px', fontSize: '13px' }}>
                {error}
              </div>
            ) : null}
          </div>
        </section>

        <aside style={{ display: 'grid', gap: '16px' }}>
          <section style={{ background: colors.bgCard, borderRadius: '18px', padding: '20px', boxShadow: CARD_SHADOW, border: `1px solid ${colors.border}` }}>
            <p className="font-body" style={{ margin: '0 0 6px', fontSize: '12px', fontWeight: 700, color: colors.textMuted, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
              Recipient Count
            </p>
            <h2 className="font-display" style={{ margin: 0, fontSize: '28px', color: colors.textPrimary }}>
              This message will go to {recipientCount} customers
            </h2>
          </section>

          <section style={{ background: colors.bgCard, borderRadius: '18px', padding: '20px', boxShadow: CARD_SHADOW, border: `1px solid ${colors.border}` }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px', marginBottom: '12px' }}>
              <div>
                <p className="font-body" style={{ margin: '0 0 4px', fontSize: '12px', fontWeight: 700, color: colors.textMuted, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                  Copyable Message
                </p>
              </div>
              <button
                type="button"
                className="font-body"
                onClick={() => void copyText(preview?.message_preview || message)}
                style={{
                  border: `1px solid ${colors.border}`,
                  background: colors.bgCard,
                  borderRadius: '999px',
                  padding: '8px 12px',
                  cursor: 'pointer',
                  fontWeight: 600,
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '8px',
                }}
              >
                <Copy size={14} /> Copy
              </button>
            </div>
            <textarea
              value={preview?.message_preview || message}
              readOnly
              className="font-body"
              style={{ width: '100%', minHeight: '140px', border: `1.5px solid ${colors.border}`, borderRadius: '14px', padding: '12px', fontSize: '13px', lineHeight: 1.6, background: colors.bgSecondary }}
            />
            {copied ? (
              <p className="font-body" style={{ margin: '8px 0 0', fontSize: '12px', color: '#15803D' }}>
                Copied.
              </p>
            ) : null}
          </section>

          <section style={{ background: colors.bgCard, borderRadius: '18px', padding: '20px', boxShadow: CARD_SHADOW, border: `1px solid ${colors.border}` }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px', marginBottom: '12px' }}>
              <p className="font-body" style={{ margin: 0, fontSize: '12px', fontWeight: 700, color: colors.textMuted, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                Recipients · open each WhatsApp manually
              </p>
              <button
                type="button"
                className="font-body"
                onClick={() => void copyText(formatPhoneList(phones.map((phone) => normalizePhone(phone))))}
                style={{
                  border: `1px solid ${colors.accent}`,
                  background: colors.surface,
                  borderRadius: '999px',
                  padding: '8px 12px',
                  cursor: 'pointer',
                  fontWeight: 700,
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '8px',
                }}
              >
                <Copy size={14} /> Copy All Numbers
              </button>
            </div>
            <div style={{ maxHeight: '260px', overflowY: 'auto', border: `1px solid ${colors.border}`, borderRadius: '14px' }}>
              {phones.length === 0 ? (
                <div className="font-body" style={{ padding: '16px', color: colors.textMuted, fontSize: '13px' }}>
                  No recipients yet. Adjust filters and enter a message to preview recipients.
                </div>
              ) : (
                phones.map((phone, index) => {
                  const recipient = recipientDetails[index]
                  return (
                    <div key={`${phone}-${index}`} style={{ display: 'flex', justifyContent: 'space-between', gap: '12px', padding: '12px 14px', borderBottom: `1px solid ${colors.border}` }}>
                      <div>
                        <div className="font-body" style={{ fontSize: '13px', fontWeight: 600, color: colors.textPrimary }}>
                          {recipient?.customer_name || 'Customer'}
                        </div>
                        <div className="font-body" style={{ fontSize: '12px', color: colors.textSecondary }}>
                          {phone}
                        </div>
                      </div>
                      {recipient?.url ? (
                        <a href={recipient.url} target="_blank" rel="noreferrer" style={{ color: '#D6B97B', textDecoration: 'none', fontWeight: 700, display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
                          <Link2 size={14} /> Open WhatsApp
                        </a>
                      ) : null}
                    </div>
                  )
                })
              )}
            </div>
          </section>
        </aside>
      </div>
    </div>
  )
}
